import { zodResolver } from "@hookform/resolvers/zod";
import { ErrorOutline, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { LoginFormData, getLoginSchema } from "../../../validation/authSchema";

interface LoginFormProps {
  onSubmit: (data: { email: string; password: string }) => void;
  isLoading?: boolean;
  onForgotPassword?: () => void;
  onRegister?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  isLoading = false,
  onForgotPassword,
  onRegister: _onRegister,
}) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(getLoginSchema()),
  });

  return (
    <Box
      component="form"
      onSubmit={handleSubmit((data) => onSubmit({ ...data }))}
    >
      <TextField
        {...register("email")}
        label={t("auth.email")}
        className="notranslate"
        translate="no"
        type="email"
        fullWidth
        autoComplete="email"
        error={!!errors.email}
        helperText={errors.email?.message}
        disabled={isLoading}
        sx={{ mb: 2 }}
      />
      <TextField
        {...register("password")}
        label={t("auth.password")}
        className="notranslate"
        translate="no"
        type={showPassword ? "text" : "password"}
        fullWidth
        autoComplete="current-password"
        error={!!errors.password}
        helperText={
          errors.password?.message 
        }
        disabled={isLoading}
        sx={{ mb: 1 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword((v) => !v)}
                edge="end"
                disabled={isLoading}
                aria-label={
                  showPassword ? t("auth.hidePassword") : t("auth.showPassword")
                }
              > {showPassword ? <VisibilityOff /> : <Visibility />}</IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2.5 }}>
        {onForgotPassword && (
          <Link
            component="button"
            type="button"
            className="notranslate"
            translate="no"
            onClick={onForgotPassword}
            disabled={isLoading}
            sx={{ fontSize: "0.813rem" }}
          >
            {t("auth.forgotPassword")}
          </Link>
        )}
      </Box>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        className="notranslate"
        translate="no"
        disabled={isLoading}
        sx={{ fontWeight: 600, py: 1.2 }}
      >
        {isLoading ? (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} color="inherit" />
            <span>{t("auth.signIn")}</span>
          </Box>
        ) : (
          t("auth.signIn")
        )}
      </Button>
    </Box>
  );
};

export default LoginForm;
